import { ControlType } from './../compiler/assemblyBlocks/inputBlocks/ControlType';
import { BlockType } from '../compiler/factory';
import { CustomRender, IfRender, WhileRender } from '../diagram/renders';
import { ShapeType, ShapeColor } from '../compiler/assemblyBlocks/shape';
import { FunctionNode, OutputNode, InputNode, IfNode, WhileNode } from '../codeBlocks';
import DiagramIDE from './DiagramIDE';

/**
 * A class that adds nodes to the schema. (For cleanness)
 */
export class NodeBuilder {
    parentSchemaId;

    /**
     * Constructor
     * @param addNode function to add node to schema.
     * @param schemaId the id of the parent schema.
     */
    constructor(schemaId: string) {
        this.parentSchemaId = schemaId
    }

    private centralizeCoordinates(coordinates: [number, number]): [number, number] {
        return [coordinates[0] - 100, coordinates[1] - 100];
    }

    private nextCoordinates(): [number, number] {
        if (!DiagramIDE.getIDE().validatedSchemas.has(this.parentSchemaId)) throw Error('Not initalized!');
        let coors = DiagramIDE.getIDE().validatedSchemas.get(this.parentSchemaId)?.getNextCoordinates();
        return coors ? coors : [0, 0];
    }

    /**
     * Creates a block with this block into the diagram.
     * @param type type of block created
     * @param coordinates the coordinates where to make
     */
    makeFunction(type: BlockType, coordinates: [number, number] = this.nextCoordinates()) {
        coordinates = this.centralizeCoordinates(coordinates);

        switch (type) {
            case BlockType.SUM:
                this.addFunctionNode('Adder', type, [], 2, 1, ['In', 'In'], ['Out'], coordinates);
                break;
            case BlockType.PROD:
                this.addFunctionNode('Multiply', type, [], 2, 1, ['In', 'In'], ['Out'], coordinates);
                break;
            case BlockType.SUBTRACT:
                this.addFunctionNode('Subtract', type, [], 2, 1, ['In', 'In'], ['Out'], coordinates);
                break;
            case BlockType.DIV:
                this.addFunctionNode('Divide', type, [], 2, 1, ['In', 'In'], ['Out'], coordinates);
                break;
            case BlockType.EQ:
                this.addFunctionNode('==', type, [], 2, 1, ['Left', 'Right'], ['Out'], coordinates);
                break;
            case BlockType.LT:
                this.addFunctionNode('<', type, [], 2, 1, ['Left', 'Right'], ['Out'], coordinates);
                break;
            case BlockType.GT:
                this.addFunctionNode('>', type, [], 2, 1, ['Left', 'Right'], ['Out'], coordinates);
                break;
            case BlockType.LEQ:
                this.addFunctionNode('<=', type, [], 2, 1, ['Left', 'Right'], ['Out'], coordinates);
                break;
            case BlockType.GEQ:
                this.addFunctionNode('>=', type, [], 2, 1, ['Left', 'Right'], ['Out'], coordinates);
                break;
            case BlockType.AND:
                this.addFunctionNode('AND', type, [], 2, 1, ['Left', 'Right'], ['Out'], coordinates);
                break;
            case BlockType.OR:
                this.addFunctionNode('OR', type, [], 2, 1, ['Left', 'Right'], ['Out'], coordinates);
                break;
            case BlockType.NOT:
                this.addFunctionNode('NOT', type, [], 1, 1, ['in'], ['Out'], coordinates);
                break;
            case BlockType.TERNARY:
                this.addFunctionNode('Ternary', type, [], 3, 1, ['If', 'Then', 'Else'], ['Out'], coordinates);
                break;
            case BlockType.MOD:
                this.addFunctionNode('Modulo %', type, [], 2, 1, ['In', 'In'], ['Out'], coordinates);
                break;
            case BlockType.EXP:
                this.addFunctionNode('Exponent', type, [], 2, 1, ['In', 'In'], ['Out'], coordinates);
                break;
            case BlockType.GET_X:
                this.addFunctionNode('Get X', type, [], 1, 1, ['Shape'], ['X'], coordinates);
                break;
            case BlockType.GET_Y:
                this.addFunctionNode('Get Y', type, [], 1, 1, ['Shape'], ['Y'], coordinates);
                break;
            default:
                console.error('This type is not supported by makeFunction')
        }

    }

    /**
     * Creates a block with this block type.
     * @param {BlockType} type type of block created
     * @param coordinates the coordinates where to make
     */
    makeInput(type: BlockType, coordinates: [number, number] = this.nextCoordinates()) {
        coordinates = this.centralizeCoordinates(coordinates);

        switch (type) {
            case BlockType.CONST:
                let cstVal = prompt('What is the value?', '0');
                this.addInputNode(`Constant: ${cstVal}`, type, [cstVal], 1, ['Value'], coordinates);
                break;
            case BlockType.VAR_RETRIEVE:
                let varName = prompt('What is the name of the variable?');
                this.addInputNode(`Get ${varName}`, type, [varName], 1, ['Value'], coordinates);
                break;
            case BlockType.GET_SHAPE:
                let shapeget = prompt('What shape name?');
                this.addInputNode(`Get ${shapeget}`, type, [shapeget], 1, ['Shape'], coordinates);
                break;
            case BlockType.JOY_X:
                let joyxname = prompt('What joystick name?', 'joystick');
                this.addInputNode(`Get ${joyxname} X`, type, [joyxname], 1, ['X'], coordinates);
                break;
            case BlockType.JOY_Y:
                let joyyname = prompt('What joystick name?', 'joystick');
                this.addInputNode(`Get ${joyyname} Y`, type, [joyyname], 1, ['Y'], coordinates);
                break;
            case BlockType.ARR_GET_IX:
                let arrName = prompt('What is the array name');
                this.addInputNode(`Get ${arrName}`, type, [arrName], 1, ['Value'], coordinates);
                break;
            default:
                console.error('This type is not supported by makeInput')
                return;
        }
    }

    /**
     * Creates an output block.
     * @param {BlockType} type type of block
     * @param coordinates the coordinates
     */
    makeOutput(type: BlockType, coordinates: [number, number] = this.nextCoordinates()) {
        coordinates = this.centralizeCoordinates(coordinates);

        switch (type) {
            case BlockType.CLB:
                this.addOutputNode('Console', type, [], 1, ['Value'], coordinates);
                break;
            case BlockType.VAR_ASSIGN:
                let varName = prompt('Pick a variable name');
                this.addOutputNode(`Assign ${varName}`, type, [varName], 1, ['Value'], coordinates);
                break;
            case BlockType.VAR_DELETE:
                let varDeleteName = prompt('Pick a variable name to delete');
                this.addOutputNode(`Delete ${varDeleteName}`, type, [varDeleteName], 0, [], coordinates);
                break;
            case BlockType.MAKE_SHAPE:
                let shapeType = prompt('Pick a type!', ShapeType.RECT);
                let color = prompt('Pick a color (rgb value)', ShapeColor.BLUE);
                this.addOutputNode(`Make ${shapeType}`, type, [shapeType, color], 6, ['Name', 'X', 'Y', 'Stretch-X', 'Stretch-Y', 'Rotation'], coordinates);
                break;
            case BlockType.ROTATE_SHAPE:
                this.addOutputNode('Rotate', type, [], 2, ['Shape', 'Rotation'], coordinates);
                break;
            case BlockType.MOVE_SHAPE:
                this.addOutputNode('Move', type, [], 3, ['Shape', 'X', 'Y'], coordinates);
                break;
            case BlockType.SCALE_SHAPE:
                this.addOutputNode('Stretch', type, [], 3, ['Shape', 'X', 'Y'], coordinates);
                break;
            case BlockType.ARR_CREATE:
                let arrMake = prompt('What is the name of array?');
                this.addOutputNode(`Create ${arrMake}`, type, [arrMake], 0, [], coordinates);
                break;
            case BlockType.ARR_SET_IX:
                let arrName = prompt('What is the name of array?');
                this.addOutputNode(`Set ${arrName}`, type, [arrName], 2, ['Index', 'Value'], coordinates);
                break;
            case BlockType.MAKE_CONTROL_ELEM:
                let elemType = prompt('Pick element type', ControlType.JOYSTICK);
                let elemName = prompt('Pick element name', 'joystick');
                this.addOutputNode(`Make ${elemName}`, type, [elemType, elemName], 2, ['X', 'Y'], coordinates);
                break;
            default:
                console.error('This node is not supported by makeOutput');
                return;
        }
    }

    makeSpecial(type: BlockType, coordinates: [number, number] = this.nextCoordinates()) {
        coordinates = this.centralizeCoordinates(coordinates);

        switch (type) {
            case BlockType.IF:
                const nextIf = new IfNode('IF',
                    coordinates,
                    [],
                    {
                        parentSchema: this.parentSchemaId,
                        inputNames: ['Condition'],
                        problematic: false,
                    },
                    IfRender);

                DiagramIDE.getIDE().schemaMethods.get(this.parentSchemaId)?.addNode(nextIf);

                break;
            case BlockType.WHILE:
                const nextWhile = new WhileNode('WHILE',
                    coordinates,
                    [],
                    {
                        parentSchema: this.parentSchemaId,
                        inputNames: ['Condition'],
                        problematic: false,
                    },
                    WhileRender);
                DiagramIDE.getIDE().schemaMethods.get(this.parentSchemaId)?.addNode(nextWhile);
                break
            default:
                console.error('This node is not supported by makeSpecial')
                return;
        }
    }

    /**
     * Adds an input node.
     * @param {string} name name of the node
     * @param {BlockType} type type of block
     * @param {any[]} constVal constant values
     * @param {number} numOfOutputs the number of outputs of this nod
     * @param {string[]} outputNames the names of each output of this node
     * @param coordinates the coordinates of this node. 
     */
    addInputNode(name: {} | null | undefined, type: BlockType, constVal: any[], numOfOutputs: number, outputNames: string[] = [], coordinates: [number, number]) {
        const nextNode = new InputNode(name,
            coordinates,
            numOfOutputs,
            type,
            constVal,
            { parentSchema: this.parentSchemaId, outputNames: outputNames, problematic: false },
            CustomRender);

        DiagramIDE.getIDE().schemaMethods.get(this.parentSchemaId)?.addNode(nextNode);
    }

    /**
     * Makes an output node
     * @param {string} name name
     * @param {BlockType} type type of block this is
     * @param {any[]} constVal constant values
     * @param {number} numOfInputs number of inputs
     * @param {string[]} inputNames the names of each input to this node
     * @param coordinates the coordinates of the node
     */
    addOutputNode(name: string, type: BlockType, constVal: any[], numOfInputs: number, inputNames: string[], coordinates: [number, number]) {
        const nextNode = new OutputNode(name,
            coordinates,
            numOfInputs,
            type,
            constVal,
            { parentSchema: this.parentSchemaId, inputNames: inputNames, problematic: false },
            CustomRender);

        DiagramIDE.getIDE().schemaMethods.get(this.parentSchemaId)?.addNode(nextNode);

    }

    /**
     * Makes a function node
     * @param {string} name name of the node
     * @param {BlockType} type type of block
     * @param {any[]} constVal constant values
     * @param {number} numOfInputs number of inputs
     * @param {number} numOfOutputs number of outputs
     * @param {string[]} inputNames the names of each input to this node
     * @param {string[]} outputNames the names of each output of this node
     */
    addFunctionNode(name: string, type: BlockType, constVal: any[], numOfInputs: number, numOfOutputs: number, inputNames: string[], outputNames: string[], coordinates: [number, number]) {
        const nextNode = new FunctionNode(name,
            coordinates,
            numOfInputs,
            numOfOutputs,
            type,
            constVal,
            { parentSchema: this.parentSchemaId, inputNames: inputNames, outputNames: outputNames, problematic: false },
            CustomRender);

        DiagramIDE.getIDE().schemaMethods.get(this.parentSchemaId)?.addNode(nextNode);
    }



}